import animationData from "../../src/assets/lottie-react.json";
import { TEST_CATEGORY_MAP, CALCULATED_TESTS } from "./TestNames";

export const animationDefaultOptions = {
  loop: true,
  autoplay: true,
  animationData: animationData,
};

export function getTestCategoryName(prefix) {
  return TEST_CATEGORY_MAP[prefix] || prefix;
}


export function buildTestGroups(fullTestList, testData) {
  if (!Array.isArray(fullTestList) || !Array.isArray(testData)) return [];

  // 1. Map ParameterID to testData object for quick lookup
  const testDataMap = {};
  testData.forEach((t) => {
    if (t?.ParameterID) {
      testDataMap[t.ParameterID] = t;
    }
  });

  // 2. Group fullTestList by prefix (first 2 letters of ParameterID)
  const prefixMap = {};
  fullTestList.forEach((test) => {
    const paramId = test.ParameterID;
    if (!paramId) return;

    const prefix = paramId.slice(0, 2);
    if (!prefixMap[prefix]) prefixMap[prefix] = [];
    prefixMap[prefix].push(test);
  });

  // 3. Build groups from prefixMap
  const groups = [];
  let groupName = ''
  Object.entries(prefixMap).forEach(([prefix, tests]) => {
    if(prefix)
    {
     groupName = getTestCategoryName(prefix)
    }
    if (tests.length === 1) {
      // Single test → make it a standalone group with itself as subTest
      const test = tests[0];
      const testDataEntry = testDataMap[test.ParameterID];

      groups.push({
        id: test.ParameterID,
        name: testDataEntry?.name || test.name,
        checked: !!testDataEntry, // Checked if it exists in testData
        expanded: true,
        subTests: [
          {
            id: test.ParameterID,
            name: testDataEntry?.name || test.name || test.ParameterID,
            checked: !!testDataEntry,
          },
        ],
      });
    } else {
      // Multiple tests → make a group
      const subTests = tests.map((test) => {
        const testDataEntry = testDataMap[test.ParameterID];
        return {
          id: test.ParameterID,
          name: testDataEntry?.name || test.name,
          checked: !!testDataEntry,
        };
      });

      groups.push({
        id: `${prefix}_group`,
        name: `${groupName} Tests`,
        checked: subTests.some((st) => st.checked),
        expanded: false,
        subTests,
      });
    }
  });

  return groups;
}

export function getMinimalSelectedTestsInfoGrouped(selectedGroups, testData) {
  const selectedIdSet = new Set(
    (selectedGroups || []).flatMap((group) => group?.options || []).filter(Boolean)
  );

  const canComputeCalculated = (parameterId, visiting = new Set()) => {
    const calc = CALCULATED_TESTS[parameterId];
    if (!calc) {
      // Non-calculated dependency must be explicitly selected
      return selectedIdSet.has(parameterId);
    }

    if (visiting.has(parameterId)) return false; // guard cycles
    visiting.add(parameterId);

    const deps = Array.isArray(calc.dependsOn) ? calc.dependsOn : [];
    const ok = deps.every((dep) => {
      if (selectedIdSet.has(dep)) return true;
      // Allow chained calculated deps (e.g., KI6501 depends on KI9413) as long as their own deps are satisfied
      if (CALCULATED_TESTS[dep]) {
        return canComputeCalculated(dep, visiting);
      }
      return false;
    });

    visiting.delete(parameterId);
    return ok;
  };

  const shouldIncludeCalculated = (parameterId) => {
    const calc = CALCULATED_TESTS[parameterId];
    if (!calc) return true;
    return canComputeCalculated(parameterId);
  };

  // Helper to pick only needed fields
  function pickFields(test, groupName) {
    return {
      inputType: test.inputType,
      inputFieldType: test.inputFieldType,
      name: test.name,
      unit: test.unit,
      ParameterID: test.ParameterID,
      autoFetchKey:test.autoFetchKey,
      referenceRange: test.referenceRange,
      subTests: test.subTest,
      groupName: groupName,
    };
  }

  return selectedGroups.map((group) => ({
    name: group.name,
    options: group.options
      .map((code) => {
        if (!shouldIncludeCalculated(code)) return null;
        const test = testData.find((t) => t.ParameterID === code);
        return test ? pickFields(test, group.name) : null;
      })
      .filter(Boolean),
  }));
}

export function sortTestsByPrefix(tests = []) {
  const prefixOrder = Object.keys(TEST_CATEGORY_MAP);

  return [...tests].sort((a, b) => {
    const pa = a.ParameterID.slice(0, 2);
    const pb = b.ParameterID.slice(0, 2);

    const ia = prefixOrder.indexOf(pa);
    const ib = prefixOrder.indexOf(pb);

    // Prefix exists only on A → A comes first
    if (ia !== -1 && ib === -1) return -1;
    if (ia === -1 && ib !== -1) return 1;

    // If prefix positions differ → sort by prefix order
    if (ia !== ib) return ia - ib;

    // SAME PREFIX → KEEP ORIGINAL API ORDER
    return 0;
  });
}


export function extractParameterIds(tests = []) {
  // console.log(tests, "---1---tests");
  if (!tests || !Array.isArray(tests)) return [];

  return tests
    .flatMap((test) => test.options)
    .map((option) => option.ParameterID)
    .filter(Boolean);
}

export function generateBillItems(tests, priceData, setToatalCalculate) {
  const priceMap = {};
  priceData.forEach((p) => {
    priceMap[p.ParameterID] = {
      basePrice: p.basePrice,
      margin: p.margin,
      strikePrice: p.strikePrice,
    };
  });

  const grouped = {};
  let grandTotal = 0;

  tests.forEach((test) => {
    const price = priceMap[test.ParameterID];
    if (!price) return; // skip if no price found

    const prefix = test.ParameterID.slice(0, 2); // e.g., 'OT', 'KI'
    const categoryName = getTestCategoryName(prefix);

    if (!grouped[prefix]) {
      grouped[prefix] = {
        testFieldName: categoryName,
        totalSubfields: [],
        totalPrice: 0,
      };
    }

    const subfield = {
      fieldName: test.name || test.ParameterID,
      fieldPrice: price.strikePrice ?? 0,
    };

    grouped[prefix].totalSubfields.push(subfield);
    grouped[prefix].totalPrice += subfield.fieldPrice;
    grandTotal += subfield.fieldPrice;
  });

  if (typeof setToatalCalculate === "function") {
    setToatalCalculate(grandTotal);
  }

  return Object.values(grouped);
}


// Define your mapping of test‑codes to some IDs or weights
export const TEST_CODES = {
  KI8734: 57, // Urea
  KI3990: 83, // Uric Acid
  KI3384: 24, // Sodium
  KI2682: 69, // Potassium
  KI5836: 45, // Total Protein
  KI5376: 31, // Blood Urea Nitrogen
  KI9527: 78, // Ratio BUN: Creatinine
  KI0036: 16, // Albumin
// …  return TEST_CODES[code] ?? null;
}

export function sampleCodeValue(codes = []) {
 if (!Array.isArray(codes) || codes.length === 0) {
   return null;
 }
 const idx = Math.floor(Math.random() * codes.length);
 const code = codes[idx];
 return TEST_CODES[code] ?? null;
}
